import { forwardRef, useState, type TextareaHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  helper?: string;
  error?: string;
  showCount?: boolean;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, helper, error, showCount, className, value, defaultValue, onChange, id, ...rest },
  ref,
) {
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState<string>(
    typeof defaultValue === 'string' ? defaultValue : '',
  );
  const currentValue = isControlled ? String(value ?? '') : internal;

  return (
    <div className="block w-full">
      {label && (
        <label htmlFor={id} className="mb-2 block text-sm font-medium text-text">
          {label}
        </label>
      )}
      <div
        className={cn(
          'relative rounded-xl border bg-surface-elevated transition-colors',
          error
            ? 'border-danger'
            : 'border-border focus-within:border-primary',
        )}
      >
        <textarea
          ref={ref}
          id={id}
          value={isControlled ? (value as string | number | readonly string[] | undefined) : undefined}
          defaultValue={isControlled ? undefined : (defaultValue as string | number | readonly string[] | undefined)}
          onChange={(e) => {
            if (!isControlled) setInternal(e.target.value);
            onChange?.(e);
          }}
          className={cn(
            'block w-full resize-none rounded-xl bg-transparent px-4 py-3 font-mono text-[0.95rem] leading-6 text-text',
            'placeholder:text-text-muted focus:outline-none',
            showCount && 'pb-7',
            className,
          )}
          {...rest}
        />
        {showCount && (
          <span
            className="pointer-events-none absolute bottom-2 right-3 select-none font-mono text-xs text-text-muted"
            aria-hidden="true"
          >
            {currentValue.length}
          </span>
        )}
      </div>
      {(helper || error) && (
        <span className={cn('mt-2 block text-xs', error ? 'text-danger' : 'text-text-muted')}>
          {error ?? helper}
        </span>
      )}
    </div>
  );
});
